import type { TypePolicies } from '@apollo/client';
import { relayStylePagination } from '@apollo/client/utilities';

import type { StrictTypedTypePolicies } from '../../generated/apollo-helpers';
import { operationsFieldPolicy } from './operationsFieldPolicy';

export const typePolicies: TypePolicies = {
  Vault: {
    fields: {
      wallets: relayStylePagination(),
    },
  },
  Query: {
    fields: {
      operations: operationsFieldPolicy,
      // CustodyAUC has no id, so without merging, a query selecting some of its
      // fields drops the fields another query cached on the same inline object.
      custodyAUC: { merge: true },
    },
  },
  // KYC is an id-less namespace that only Query.kyc reaches, so sibling `kyc { … }` results must merge, not replace each other.
  // This is the correct model, not a workaround: there is no backend identity to normalize on.
  KYC: {
    merge: true,
  },
  // Portfolio has no id and lives inside its Vault or Organization. Several queries on one page select
  // different Portfolio fields (header balance, overview breakdown, NFT collections), and a replace
  // would drop each other's fields, so each watched query refetches in turn and the page never settles.
  Portfolio: {
    merge: true,
  },
  // Id-less params under a normalized GDFControlAgreement. The agreement fragment and the terms query
  // each select a different subset of `oes { … }`, so the subsets must merge, not replace each other.
  GDFOESAgreementParams: {
    merge: true,
  },
  // Amount is id-less. The OES agreement fragment selects a smaller subset of the agreement's
  // `totalDelegatedAssetValueUSD { … }` than the control agreement fragment, so the subsets must merge.
  GDFControlAgreement: {
    fields: {
      totalDelegatedAssetValueUSD: {
        merge: true,
      },
    },
  },
  // The backend repeats quorum ids across policies, so normalizing merges different policies' quorums.
  // Delete this and its proof test once quorum ids are unique; if PolicyQuorum stops being a Node instead, this override is the correct model.
  PolicyQuorum: {
    keyFields: false,
  },
  // The backend keeps a binding's id across policy generations, so normalizing merges one generation's quorums into another.
  // Delete this and its proof test once binding ids are generation-aware; if PolicyFragmentBinding stops being a Node instead, this override is the correct model.
  PolicyFragmentBinding: {
    keyFields: false,
  },
} satisfies StrictTypedTypePolicies;
